/*
================================================================================
FILE HEADER — packages/ir/src/schemas.ts
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                    | What Changed          |
|----|------------|-------------------|------------------------|-----------------------|
| 1  | 2026-06-23 | Claude Sonnet 4.6 | Phase 0 bootstrap      | Created full IR Zod   |
|    |            |                   |                        | schema definitions    |
| 2  | 2026-07-02 | Claude Sonnet 5   | Phase 6 AI Assist mode | Added 'assist' to     |
|    |            |                   |                        | Candidate.addedBy —   |
|    |            |                   |                        | tags candidates auto- |
|    |            |                   |                        | resolved from typed   |
|    |            |                   |                        | plain-English steps   |

FILES THAT IMPORT THIS FILE
-----------------------------
- packages/ir/src/index.ts  (re-exports everything)
- packages/dal/src/index.ts (imports A11yNode types)
- packages/runner/src/index.ts (interprets IRStep at runtime)
- apps/studio-web/src/...   (uses schemas for validation)
- apps/plex-extension/background.ts (constructs IRStep on record)

EXPORTS COVERED
---------------
- CandidateStrategySchema / CandidateStrategy
- FingerprintSchema / Fingerprint
- SelectorSchema / Selector
- CandidateSchema / Candidate
- All step schemas (OpenUrlStep … FrameScopeStep)
- IRStepSchema / IRStep (discriminated union of all step types)
- FlowSchema / Flow
- TestIRSchema / TestIR  (top-level document)

CALLER → CALLEE MAP
--------------------
- TestIRSchema uses FlowSchema
- FlowSchema uses IRStepSchema
- IRStepSchema (lazy) references itself via IfStep/RepeatStep/ForEachRowStep/FrameScopeStep
- All interactive steps use SelectorSchema
- SelectorSchema uses CandidateSchema (lazy, recursive via `within`)

IMPORTANT POINTS
----------------
1. SelectorSchema and CandidateSchema are mutually recursive — must use z.lazy().
2. Control-flow steps (if/repeat/forEachRow) and frameScope embed IRStep[] —
   also recursive, use z.lazy() pointing at IRStepSchema.
3. zod-to-json-schema generates packages/ir/schemas/ir-1.0.json from these
   schemas. Never edit that JSON file by hand.
4. `irVersion` gates breaking IR changes; add a migration in migrate.ts for
   each new version.
5. `sensitive: true` on typeText means the `text` value must never appear in
   logs, screenshots, or reports — enforced at runner level.

ISSUES FACED / KNOWN GOTCHAS
------------------------------
1. z.lazy() inside z.discriminatedUnion() is not directly supported in Zod 3.x.
   Workaround: build the non-recursive union first, then wrap recursive variants
   with z.lazy() and combine with z.union() at the IRStepSchema level.
2. `exactOptionalPropertyTypes: true` in tsconfig means optional fields with
   `undefined` must use `z.optional()` not `.optional()` — same thing in Zod,
   but be careful when spreading partial objects at runtime.
================================================================================
*/

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Candidate Strategy
// ---------------------------------------------------------------------------

export const CandidateStrategySchema = z.enum([
  'a11yId',
  'text',
  'textContains',
  'textRegex',
  'contentDesc',
  'className',
  'css',
  'xpath',
  'relative',
]);

export type CandidateStrategy = z.infer<typeof CandidateStrategySchema>;

// ---------------------------------------------------------------------------
// Fingerprint — Tier-1 healing data; pure data, no model refs at schema level
// ---------------------------------------------------------------------------

export const FingerprintSchema = z.object({
  semanticText: z.string(),
  embeddingRef: z.string().optional(),
  cropRef: z.string().optional(),
  boundsRatio: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  neighbors: z.object({
    above: z.string().optional(),
    below: z.string().optional(),
    left: z.string().optional(),
    right: z.string().optional(),
  }),
  screenSignature: z.string(),
});

export type Fingerprint = z.infer<typeof FingerprintSchema>;

// ---------------------------------------------------------------------------
// Selector + Candidate (mutually recursive via z.lazy)
// ---------------------------------------------------------------------------

export type Candidate = {
  strategy: CandidateStrategy;
  value: string;
  index?: number;
  within?: Selector;
  addedBy: 'recorder' | 'user' | 'heal' | 'assist';
};

export type Selector = {
  description: string;
  candidates: Candidate[];
  fingerprint?: Fingerprint;
};

export const CandidateSchema: z.ZodType<Candidate> = z.lazy(() =>
  z.object({
    strategy: CandidateStrategySchema,
    value: z.string(),
    index: z.number().int().min(0).optional(),
    within: SelectorSchema.optional(),
    addedBy: z.enum(['recorder', 'user', 'heal', 'assist']),
  })
);

export const SelectorSchema: z.ZodType<Selector> = z.lazy(() =>
  z.object({
    description: z.string(),
    candidates: z.array(CandidateSchema).min(1),
    fingerprint: FingerprintSchema.optional(),
  })
);

// ---------------------------------------------------------------------------
// Shared base fields present on every step
// ---------------------------------------------------------------------------

const StepBase = z.object({
  id: z.string(),
  label: z.string().optional(),
  platformOverride: z.record(z.unknown()).optional(),
});

// ---------------------------------------------------------------------------
// Navigation steps
// ---------------------------------------------------------------------------

export const OpenUrlStepSchema = StepBase.extend({ type: z.literal('openUrl'), url: z.string().url() });
export const DeepLinkStepSchema = StepBase.extend({ type: z.literal('deepLink'), url: z.string(), package: z.string().optional() });
export const LaunchAppStepSchema = StepBase.extend({ type: z.literal('launchApp'), clearState: z.boolean().optional() });
export const StopAppStepSchema = StepBase.extend({ type: z.literal('stopApp') });

// ---------------------------------------------------------------------------
// Interaction steps
// ---------------------------------------------------------------------------

export const TapStepSchema = StepBase.extend({ type: z.literal('tap'), selector: SelectorSchema });
export const DoubleTapStepSchema = StepBase.extend({ type: z.literal('doubleTap'), selector: SelectorSchema });
export const LongPressStepSchema = StepBase.extend({ type: z.literal('longPress'), selector: SelectorSchema, durationMs: z.number().min(0).default(500) });
export const TypeTextStepSchema = StepBase.extend({ type: z.literal('typeText'), text: z.string(), sensitive: z.boolean().optional() });
export const ClearTextStepSchema = StepBase.extend({ type: z.literal('clearText'), selector: SelectorSchema });
export const PressKeyStepSchema = StepBase.extend({ type: z.literal('pressKey'), key: z.enum(['back', 'home', 'enter', 'tab', 'delete']) });
export const HideKeyboardStepSchema = StepBase.extend({ type: z.literal('hideKeyboard') });
export const SwipeStepSchema = StepBase.extend({
  type: z.literal('swipe'),
  from: z.tuple([z.number(), z.number()]),
  to: z.tuple([z.number(), z.number()]),
  durationMs: z.number().min(0).default(300),
});
export const HoldSwipeStepSchema = StepBase.extend({
  type: z.literal('holdSwipe'),
  selector: SelectorSchema,
  direction: z.enum(['up', 'down', 'left', 'right']),
  holdMs: z.number().min(0).default(500),
  distance: z.number().min(0).max(1).default(0.5),
});
export const ScrollUntilVisibleStepSchema = StepBase.extend({
  type: z.literal('scrollUntilVisible'),
  selector: SelectorSchema,
  direction: z.enum(['up', 'down', 'left', 'right']).default('down'),
  maxScrolls: z.number().int().min(1).default(10),
});

// ---------------------------------------------------------------------------
// Waiting steps
// ---------------------------------------------------------------------------

export const WaitStepSchema = StepBase.extend({ type: z.literal('wait'), durationMs: z.number().min(0) });
export const WaitForStepSchema = StepBase.extend({
  type: z.literal('waitFor'),
  selector: SelectorSchema,
  state: z.enum(['visible', 'hidden', 'enabled', 'disabled']).default('visible'),
  timeoutMs: z.number().min(0).optional(),
});
export const WaitForNetworkStepSchema = StepBase.extend({
  type: z.literal('waitForNetwork'),
  urlPattern: z.string().optional(),
  timeoutMs: z.number().min(0).optional(),
});

// ---------------------------------------------------------------------------
// Assertion steps
// ---------------------------------------------------------------------------

export const AssertStepSchema = StepBase.extend({
  type: z.literal('assert'),
  selector: SelectorSchema,
  assertion: z.enum(['visible', 'notVisible', 'enabled', 'disabled', 'hasText']),
  value: z.string().optional(),
});
export const NetworkAssertStepSchema = StepBase.extend({
  type: z.literal('networkAssert'),
  method: z.string().optional(),
  urlPattern: z.string(),
  expectedStatus: z.number().int().optional(),
  expectedBodyContains: z.string().optional(),
  captureAs: z.string().optional(),
});
export const VisualAssertStepSchema = StepBase.extend({
  type: z.literal('visualAssert'),
  mode: z.enum(['screenshot-diff', 'llm-describe']),
  threshold: z.number().min(0).max(1).optional(),
  baselineRef: z.string().optional(),
});
export const CheckpointStepSchema = StepBase.extend({
  type: z.literal('checkpoint'),
  message: z.string(),
  status: z.enum(['pass', 'fail']),
});

// ---------------------------------------------------------------------------
// Data steps
// ---------------------------------------------------------------------------

const VariableSourceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('literal'), value: z.string() }),
  z.object({ kind: z.literal('capture'), selector: SelectorSchema }),
  z.object({ kind: z.literal('attribute'), selector: SelectorSchema, attribute: z.string() }),
  z.object({ kind: z.literal('count'), selector: SelectorSchema }),
  z.object({ kind: z.literal('random'), pattern: z.string().optional() }),
]);

export const SetVariableStepSchema = StepBase.extend({ type: z.literal('setVariable'), name: z.string(), source: VariableSourceSchema });
export const GetAttributeStepSchema = StepBase.extend({ type: z.literal('getAttribute'), selector: SelectorSchema, attribute: z.string(), storeAs: z.string() });
export const GetCountStepSchema = StepBase.extend({ type: z.literal('getCount'), selector: SelectorSchema, storeAs: z.string() });
export const EvaluateStepSchema = StepBase.extend({
  type: z.literal('evaluate'),
  left: z.string(),
  operator: z.enum(['==', '!=', '>', '<', '>=', '<=', 'contains', 'notContains', 'startsWith', 'endsWith', 'matches', 'isEmpty', 'isNotEmpty']),
  right: z.string().optional(),
  storeAs: z.string().optional(),
  failIfFalse: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Developer escape hatches
// ---------------------------------------------------------------------------

export const ScriptStepSchema = StepBase.extend({ type: z.literal('script'), code: z.string(), storeAs: z.string().optional() });
export const ApiStepSchema = StepBase.extend({
  type: z.literal('api'),
  method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
  url: z.string(),
  headers: z.record(z.string()).optional(),
  body: z.unknown().optional(),
  storeAs: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Media / reporting steps
// ---------------------------------------------------------------------------

export const ScreenshotStepSchema = StepBase.extend({ type: z.literal('screenshot') });
export const PrintStepSchema = StepBase.extend({ type: z.literal('print'), message: z.string(), level: z.enum(['info', 'warn', 'error']).default('info') });
export const StopTestStepSchema = StepBase.extend({ type: z.literal('stopTest'), message: z.string().optional(), status: z.enum(['passed', 'failed']) });

// ---------------------------------------------------------------------------
// Browser-specific steps
// ---------------------------------------------------------------------------

export const SetDeviceStateStepSchema = StepBase.extend({
  type: z.literal('setDeviceState'),
  wifi: z.boolean().optional(),
  airplane: z.boolean().optional(),
  darkMode: z.boolean().optional(),
  location: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Control-flow steps + FrameScope — recursive, use z.lazy
// ---------------------------------------------------------------------------

// IRStep is the recursive union. We define the type first, then the schema.
export type IRStep =
  | z.infer<typeof OpenUrlStepSchema>
  | z.infer<typeof DeepLinkStepSchema>
  | z.infer<typeof LaunchAppStepSchema>
  | z.infer<typeof StopAppStepSchema>
  | z.infer<typeof TapStepSchema>
  | z.infer<typeof DoubleTapStepSchema>
  | z.infer<typeof LongPressStepSchema>
  | z.infer<typeof TypeTextStepSchema>
  | z.infer<typeof ClearTextStepSchema>
  | z.infer<typeof PressKeyStepSchema>
  | z.infer<typeof HideKeyboardStepSchema>
  | z.infer<typeof SwipeStepSchema>
  | z.infer<typeof HoldSwipeStepSchema>
  | z.infer<typeof ScrollUntilVisibleStepSchema>
  | z.infer<typeof WaitStepSchema>
  | z.infer<typeof WaitForStepSchema>
  | z.infer<typeof WaitForNetworkStepSchema>
  | z.infer<typeof AssertStepSchema>
  | z.infer<typeof NetworkAssertStepSchema>
  | z.infer<typeof VisualAssertStepSchema>
  | z.infer<typeof CheckpointStepSchema>
  | z.infer<typeof SetVariableStepSchema>
  | z.infer<typeof GetAttributeStepSchema>
  | z.infer<typeof GetCountStepSchema>
  | z.infer<typeof EvaluateStepSchema>
  | z.infer<typeof ScriptStepSchema>
  | z.infer<typeof ApiStepSchema>
  | z.infer<typeof ScreenshotStepSchema>
  | z.infer<typeof PrintStepSchema>
  | z.infer<typeof StopTestStepSchema>
  | z.infer<typeof SetDeviceStateStepSchema>
  | {
      type: 'if';
      id: string;
      label?: string;
      platformOverride?: Record<string, unknown>;
      condition: { selector: Selector; assertion: 'visible' | 'notVisible' | 'enabled' | 'disabled' };
      then: IRStep[];
      else?: IRStep[];
    }
  | {
      type: 'repeat';
      id: string;
      label?: string;
      platformOverride?: Record<string, unknown>;
      count: number;
      steps: IRStep[];
    }
  | {
      type: 'forEachRow';
      id: string;
      label?: string;
      platformOverride?: Record<string, unknown>;
      dataSource: string;
      variable: string;
      steps: IRStep[];
    }
  | {
      type: 'callFlow';
      id: string;
      label?: string;
      platformOverride?: Record<string, unknown>;
      sharedFlowId: string;
      sharedFlowName: string;
      args: Record<string, string>;
    }
  | { type: 'break'; id: string; label?: string; platformOverride?: Record<string, unknown> }
  | { type: 'continue'; id: string; label?: string; platformOverride?: Record<string, unknown> }
  | {
      type: 'frameScope';
      id: string;
      label?: string;
      platformOverride?: Record<string, unknown>;
      selector: Selector;
      steps: IRStep[];
    };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const IRStepSchema: z.ZodType<IRStep, z.ZodTypeDef, any> = z.lazy(() =>
  z.discriminatedUnion('type', [
    OpenUrlStepSchema,
    DeepLinkStepSchema,
    LaunchAppStepSchema,
    StopAppStepSchema,
    TapStepSchema,
    DoubleTapStepSchema,
    LongPressStepSchema,
    TypeTextStepSchema,
    ClearTextStepSchema,
    PressKeyStepSchema,
    HideKeyboardStepSchema,
    SwipeStepSchema,
    HoldSwipeStepSchema,
    ScrollUntilVisibleStepSchema,
    WaitStepSchema,
    WaitForStepSchema,
    WaitForNetworkStepSchema,
    AssertStepSchema,
    NetworkAssertStepSchema,
    VisualAssertStepSchema,
    CheckpointStepSchema,
    SetVariableStepSchema,
    GetAttributeStepSchema,
    GetCountStepSchema,
    EvaluateStepSchema,
    ScriptStepSchema,
    ApiStepSchema,
    ScreenshotStepSchema,
    PrintStepSchema,
    StopTestStepSchema,
    SetDeviceStateStepSchema,
    // Recursive control-flow
    StepBase.extend({
      type: z.literal('if'),
      condition: z.object({
        selector: SelectorSchema,
        assertion: z.enum(['visible', 'notVisible', 'enabled', 'disabled']),
      }),
      then: z.array(IRStepSchema),
      else: z.array(IRStepSchema).optional(),
    }),
    StepBase.extend({
      type: z.literal('repeat'),
      count: z.number().int().min(1),
      steps: z.array(IRStepSchema),
    }),
    StepBase.extend({
      type: z.literal('forEachRow'),
      dataSource: z.string(),
      variable: z.string(),
      steps: z.array(IRStepSchema),
    }),
    StepBase.extend({
      type: z.literal('callFlow'),
      sharedFlowId: z.string(),
      sharedFlowName: z.string(),
      args: z.record(z.string()),
    }),
    StepBase.extend({ type: z.literal('break') }),
    StepBase.extend({ type: z.literal('continue') }),
    StepBase.extend({
      type: z.literal('frameScope'),
      selector: SelectorSchema,
      steps: z.array(IRStepSchema),
    }),
  ])
);

// ---------------------------------------------------------------------------
// Flow
// ---------------------------------------------------------------------------

export const FlowSchema = z.object({
  id: z.string(),
  name: z.string(),
  entry: z.boolean().optional(),
  steps: z.array(IRStepSchema),
});

export type Flow = z.infer<typeof FlowSchema>;

// ---------------------------------------------------------------------------
// TestIR — top-level document
// ---------------------------------------------------------------------------

export const TestIRSchema = z.object({
  irVersion: z.literal('1.0'),
  meta: z.object({
    id: z.string(),
    name: z.string(),
    tags: z.array(z.string()).default([]),
    owner: z.string().optional(),
    createdBy: z.enum(['recorder', 'sdk', 'import']),
  }),
  config: z.object({
    targets: z.array(z.enum(['web', 'android', 'ios'])).min(1),
    baseUrl: z.string().optional(),
    env: z.record(z.string()).default({}),
    dataSources: z.array(z.string()).default([]),
    interruptionPolicy: z.enum(['auto', 'fail', 'ignore']).default('auto'),
    healing: z.object({
      maxTierUsed: z.number().int().min(0).max(3).default(1),
      minConfidence: z.number().min(0).max(1).default(0.7),
    }).default({ maxTierUsed: 1, minConfidence: 0.7 }),
    timeouts: z.object({
      step: z.number().int().min(0).default(15000),
      suite: z.number().int().min(0).default(300000),
    }).default({ step: 15000, suite: 300000 }),
  }),
  flows: z.array(FlowSchema).min(1),
  hooks: z.object({
    beforeEach: z.array(IRStepSchema).default([]),
    afterEach: z.array(IRStepSchema).default([]),
  }).default({ beforeEach: [], afterEach: [] }),
});

export type TestIR = z.infer<typeof TestIRSchema>;
